import {FC, useEffect, useRef, useState} from 'react'
import {Dispatch} from 'redux'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {RootState} from '../../../../setup'
import {ReactTyped} from 'react-typed'
import {StepperComponent} from '../../../../_metronic/assets/ts/components'
import {Form, Formik, FormikValues} from 'formik'
import {
  createAccountSchemas,
  ICreateAccount,
  inits,
  dialogValidationSchemas,
} from './CreateAccountWizardHelper'
import {Step1} from './steps/Step1'
import {Step2} from './steps/Step2'
import {Step3} from './steps/Step3'
import {
  createICCompany,
  closeSuccesDialog,
  handleEnterprisePlanDialog,
  handleLoadingState,
  handlePhoneNoVerifyDialog,
  handleEmailVerifyDialog,
} from '../../../../setup/redux/auth/actions'
import Swal from 'sweetalert2'
import {EnterPriseStepModal} from './steps/popup/EnterPriseStepModal'
import {Loader} from '../../../services/Loader'
import {resetLocalStorageData} from '../../../services/Functional'

const SignUp: FC = () => {
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const [currentSchema, setCurrentSchema] = useState(createAccountSchemas[0])
  const [initValues] = useState<ICreateAccount>(inits)
  const [isSubmitButton, setSubmitButton] = useState(false)
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()

  const {config, isOpenSuccessDialog, isEnterprisePlanDialog, isLoading}: any =
    useSelector<RootState>(
      (state) => ({
        config: state.configuration.configurations,
        isOpenSuccessDialog: state.auth.isOpenSuccessDialog,
        isEnterprisePlanDialog: state.auth.isEnterprisePlanDialog,
        isLoading: state.auth.isLoading,
      }),
      shallowEqual
    )

  useEffect(() => {
    return () => {
      navigate('/logout', {replace: true})
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        submitButtonRef.current?.click()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    dispatch(handleEnterprisePlanDialog(false))
    dispatch(handleLoadingState(false))
    dispatch(handlePhoneNoVerifyDialog(false))
    dispatch(handleEmailVerifyDialog(false))
    resetLocalStorageData()
  }, [])

  useEffect(() => {
    if (isOpenSuccessDialog) {
      Swal.fire({
        title: '',
        html: '<h2>Request Submitted Successfully!</h2>',
        icon: 'success',
        allowOutsideClick: false,
      }).then(() => {
        dispatch(closeSuccesDialog(false))
        Swal.close()
        navigate('/auth/login')
      })
    }
  }, [isOpenSuccessDialog])

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(stepperRef.current as HTMLDivElement)
  }

  const prevStep = () => {
    if (!stepper.current) {
      return
    }
    stepper.current.goPrev()
    setCurrentSchema(createAccountSchemas[stepper.current.currentStepIndex - 1])
    setSubmitButton(stepper.current.currentStepIndex === stepper.current.totatStepsNumber)
  }

  const submitStep = (values: ICreateAccount, actions: FormikValues) => {
    if (!stepper.current) {
      return
    }
    if (stepper.current.currentStepIndex !== stepper.current.totatStepsNumber) {
      stepper.current.goNext()
    } else {
      if (!values.subscription_plan || !values.selected_plan) {
        actions.setFieldError('subscription_plan', 'Please select a subscription plan to continue.')
        return
      }
      const obj: any = {
        user_id: values.user_id,
        name: values.companyName,
        company_registration_number: values.companyRegistrationNumber,
        vat_number: values.vatNumber,
        email: values.email,
        phone: values.phone,
        location: values.address,
        state: values.state,
        city: values.city,
        country: values.country,
        zip_code: values.zipcode,
        country_code: values.country_code,
        is_active: true,
        subscription_plan: values.subscription_plan,
        sectors: values.sectors,
      }
      if (values.plan_type === 'paid') {
        obj.subscription_type = values.subscription_type
      }
      if (values?.selected_plan?.type === 'free') {
        dispatch(createICCompany(obj))
      } else if (values?.selected_plan?.type === 'paid') {
        dispatch(createICCompany(obj))
      } else if (values?.selected_plan?.type === 'enterprise') {
        dispatch(handleEnterprisePlanDialog(true))
      }
    }
    setSubmitButton(stepper.current.currentStepIndex === stepper.current.totatStepsNumber)
    setCurrentSchema(createAccountSchemas[stepper.current.currentStepIndex - 1])
  }

  useEffect(() => {
    if (!stepperRef.current) {
      return
    }
    loadStepper()
  }, [stepperRef])

  return (
    <>
      {isLoading && <Loader />}
      <div className='row p-0 m-0 vh-100 vw-100'>
        <div className='col-md-3 background_card'>
          <img src={config && config?.actual_logo} alt='' />
          <p className='mt-5 fs-6 fw-bold' style={{width: '170px'}}>
            Decent.H&S&nbsp;
            <ReactTyped
              strings={['Be Decent.', 'Be Safe.']}
              className='text-primary'
              typeSpeed={40}
              backSpeed={50}
              backDelay={2000}
              loop
            />
          </p>
          <h2 className='font-weight-bolder text-center mt-3 text-capitalize text-primary'>
            register your Company
          </h2>
        </div>
        <div className='col-md-9 p-0 bg-white mainSignUpStepper'>
          <div
            ref={stepperRef}
            className='stepper m-auto  stepper-links d-flex flex-column px-10 py-5 h-100'
            id='kt_create_account_stepper'
            style={{maxWidth: '80%'}}
          >
            <div className='card mb-6'>
              <div className='stepper-nav card-body pt-8 pb-6 m-0 justify-content-start'>
                <div className='stepper-item my-0 current' data-kt-stepper-element='nav'>
                  <h3 className='stepper-title'>Company Details</h3>
                </div>

                <div className='stepper-item my-0' data-kt-stepper-element='nav'>
                  <h3 className='stepper-title'>Industry</h3>
                </div>

                <div className='stepper-item my-0' data-kt-stepper-element='nav'>
                  <h3 className='stepper-title'>Subscription Plan</h3>
                </div>
              </div>
            </div>

            <Formik
              validationSchema={currentSchema}
              initialValues={initValues}
              onSubmit={submitStep}
            >
              {(formik) => {
                return (
                  <Form className='w-100 h-100 d-flex flex-column' id='kt_create_account_form'>
                    <div className='current dataScroll card px-0' data-kt-stepper-element='content'>
                      <Step1 formik={formik} />
                    </div>

                    <div data-kt-stepper-element='content' className='dataScroll card px-0'>
                      <Step2 />
                    </div>

                    <div data-kt-stepper-element='content' className='dataScroll card  px-0'>
                      <Step3 />
                    </div>

                    <div className='card mt-6'>
                      <section className='card-body py-8 d-flex justify-content-between w-100'>
                        <div className='mr-2'>
                          <a
                            onClick={() => {
                              navigate('/auth/login')
                            }}
                            type='button'
                            className='btn btn-secondary me-2'
                            data-kt-stepper-action='next'
                          >
                            {' '}
                            Cancel
                          </a>
                        </div>

                        <div>
                          <button
                            onClick={prevStep}
                            type='button'
                            className='btn btn-secondary'
                            data-kt-stepper-action='previous'
                            tabIndex={0}
                          >
                            Back
                          </button>
                          <button
                            ref={submitButtonRef}
                            type='submit'
                            className='btn btn-primary ms-5 isOpacityDisable'
                          >
                            <span className='indicator-label'>
                              {!isSubmitButton && 'Next'}
                              {isSubmitButton && 'Submit'}
                            </span>
                          </button>
                        </div>
                      </section>
                    </div>
                    <EnterPriseStepModal
                      isEnterprisePlanDialog={isEnterprisePlanDialog}
                      validationSchema={dialogValidationSchemas[0]}
                    />
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  )
}

export {SignUp}
